import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { makeStyles, Grid, Typography } from "@material-ui/core";

import { PAGES } from "../../";
import { KPIS } from "../constants";
import { PlusButton } from "../../../components/base";
import { BarChart } from "../../../components/charts";
import { ColorIndicator } from "../../../components/colorIndicator";
import { unrealizedPowerRevenueLegends } from "../sample";
import { BAR_CHART_COLORS, UNREALIZED_SHIFT_BARS, CHART_KEYS } from "../constants";
import {
  api,
  getPercent,
  currencyFormatter,
  getLastBarTotal,
  requestDateFormat,
  sumFromListOfObjects,
  generateEstimatedData,
  isResponseDataPerMonth,
} from "../../../services";
import useDiff from "../../../hooks/useDiff";
import { NoDataCard } from "../../../components/parts";

const useStyles = makeStyles((theme) => ({
  container: {
    height: "80%",
    paddingLeft: theme.spacing(4),
  },
  row: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    "& p": {
      fontSize: 12,
    },
  },
  title: {
    marginBottom: theme.spacing(1.5),
  },
}));

export const UnrealizedPowerRevenueChart = ({ activeKpi, period, startDate, endDate }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [rawData, setRawData] = useState(null);
  const [legends, setLegends] = useState(unrealizedPowerRevenueLegends);
  const [bars, setBars] = useState(UNREALIZED_SHIFT_BARS);
  const [diffDays] = useDiff(startDate, endDate);
  const currencySymbol = useSelector((state) => state.auth.user.currency.symbol);

  const handleClick = () => {
    navigate(`${PAGES.dashboard.index}/${PAGES.dashboard.unrealized}`);
  };

  useEffect(() => {
    let isDataPerMonth = true;
    let data = [];
    if (rawData) {
      const legends = unrealizedPowerRevenueLegends.map((legend) => ({
        ...legend,
        value: 0,
        percentage: 0,
      }));

      const power_revenues = rawData.power_revenues;
      const unrealized = rawData.unrealized;

      isDataPerMonth = isResponseDataPerMonth(power_revenues);
      const currentYear = new Date().getUTCFullYear();

      data = power_revenues.map((item, i) => {
        const date = new Date(item.date);
        const year = date.getUTCFullYear();

        const unrealized_item = unrealized[i];

        const trading = unrealized_item?.trading ?? 0;
        const assets = unrealized_item?.assets ?? 0;

        legends[0].value += item.power_revenue;
        legends[1].value += trading;
        legends[2].value += assets;
        legends[3].value += trading + assets;

        if (year > currentYear) {
          return {
            date: item.date,
            estimated: item.power_revenue + trading + assets,
            x: `${isDataPerMonth ? `${date.getMonth() + 1}-` : ""}${date.getUTCFullYear()}-E*`,
          };
        }
        return {
          date: item.date,
          power_revenues: item.power_revenue,
          unrealized_trading: trading,
          unrealized_power_assets: assets,
          x: `${isDataPerMonth ? `${date.getMonth() + 1}-` : ""}${date.getUTCFullYear()}`,
        };
      });

      const fields = ["power_revenues", "unrealized_trading", "unrealized_power_assets"];

      fields.forEach((item, i) => {
        legends[i].value = sumFromListOfObjects(data, item);
      });
      const total = sumFromListOfObjects(data, "power_revenues");
      legends.forEach((legend) => {
        legend.percentage = getPercent(legend.value, total);
      });
      setBars([...UNREALIZED_SHIFT_BARS]);
      setLegends(legends);
    }
    const lastBarTotal = getLastBarTotal(data, [
      "power_revenues",
      "unrealized_trading",
      "unrealized_power_assets",
    ]);
    const estimatedData = generateEstimatedData(data, lastBarTotal, isDataPerMonth, undefined);
    setData(data.concat(estimatedData));
  }, [rawData]);

  useEffect(() => {
    if (activeKpi === KPIS.unrealized_power_revenue) {
      const getData = (url) => {
        api.get(url).then((resp) => setRawData(resp.data));
      };

      const getUrl = (period) => {
        let url = "/praas/v1/dashboard/unrealized-power-revenue-graph/";
        if (period.name === "RANGE" && endDate) {
          url += `?from_date=${requestDateFormat(startDate)}&to_date=${requestDateFormat(endDate)}`;
          getData(url);
        } else if (["YTD", "Acc.", "20Y"].includes(period.name)) {
          url += `?date_filter=${period.filter}`;
          getData(url);
        }
      };

      getUrl(period);
    }
  }, [activeKpi, period, startDate, endDate]);

  return (
    <>
      <Grid className={classes.title} container alignItems="center">
        <Typography
          style={{
            fontWeight: "bold",
            marginRight: 25,
          }}
        >
          Unrealized power revenue
        </Typography>
      </Grid>
      {rawData?.power_revenues?.length ? (
        <Grid container>
          <Grid item xs={9} xl={9}>
            <BarChart chartData={data} bars={bars} period={period} dateDiff={diffDays} />
          </Grid>
          <Grid item xs={3} xl={3}>
            <Grid
              className={classes.container}
              container
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid item xs={10}>
                {legends.map((legend, index) => (
                  <Grid
                    className={classes.row}
                    key={index}
                    container
                    alignItems="center"
                    justifyContent="space-evenly"
                  >
                    <Grid item lg={8}>
                      <Grid container alignItems="center" wrap="nowrap">
                        <ColorIndicator
                          color={BAR_CHART_COLORS[legend.key]}
                          onlyBorder={legend.key === CHART_KEYS.total_unrealized}
                        />
                        <Typography>{legend.text}</Typography>
                      </Grid>
                    </Grid>
                    <Grid item lg={2}>
                      <Typography>{currencyFormatter(legend.value, currencySymbol)}</Typography>
                    </Grid>
                    <Grid item lg={2}>
                      <Typography>{legend.percentage}%</Typography>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
              <Grid item xs={2}>
                <PlusButton onClick={handleClick} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      ) : (
        <Grid>
          <NoDataCard rootStyle={{ minHeight: 200 }} gridStyle={{ height: 120 }} />
        </Grid>
      )}
    </>
  );
};
